import React, { useEffect, useState } from 'react';
import { useDashboardContext } from '../../../components/providers/DashboardProvider';
import { useAuth } from '../../../components/providers/AuthProvider';
import { Box } from '@mui/material';
import UnavailableCard from './UnavailableCard';
import RawDataPanel from '../RawDataPanel';
import CurrentCard from './CurrentCard';
import DayCard from './DayCard';
import MonthCard from './MonthCard';
import YearCard from './YearCard';

// Peak = whichever of max/min has the larger magnitude (solar production is negative,
// so max alone would pick the small positive idle draw at night). Falls back to max if min is absent.
const pickPeak = (maxVal, minVal) => {
  if (minVal == null) return maxVal ?? null;
  if (maxVal == null) return minVal;
  return Math.abs(minVal) > Math.abs(maxVal) ? minVal : maxVal;
};

const SolarPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.profile?.user_type === 'admin';

  const { pollingCounter } = useDashboardContext();

  const {
    fetchClusterSolarData, solarData,
  } = useDashboardContext();

  const [dataValid, setDataValid] = useState(false);

  const [phaseCurrent1, setPhaseCurrent1] = useState(0);
  const [phaseCurrent2, setPhaseCurrent2] = useState(0);
  const [phaseCurrent3, setPhaseCurrent3] = useState(0);
  const [powerAvg, setPowerAvg] = useState(0);
  const [powerMaxToday, setPowerMaxToday] = useState(0);
  const [energyToday, setEnergyToday] = useState(0);

  const [tsHourlyEnergy, setTsHourlyEnergy] = useState([]);
  const [tsHourlyPower, setTsHourlyPower] = useState([]);

  const [tsDailyEnergy, setTsDailyEnergy] = useState([]);
  const [tsDailyPower, setTsDailyPower] = useState([]);

  const [tsMonthlyEnergy, setTsMonthlyEnergy] = useState([]);
  const [tsMonthlyPower, setTsMonthlyPower] = useState([]);


  const runRoutine = async () => {
    console.log("Polling in Solar");
    fetchClusterSolarData();
  }

  function processSolarData(solarData) {
    // console.log(solarData);

    setPhaseCurrent1(0);
    setPhaseCurrent2(0);
    setPhaseCurrent3(0);
    setPowerAvg(0);
    setPowerMaxToday(0);
    setEnergyToday(0);

    setTsHourlyEnergy([]);
    setTsHourlyPower([]);

    setTsDailyEnergy([]);
    setTsDailyPower([]);

    setTsMonthlyEnergy([]);
    setTsMonthlyPower([]);

    if (solarData && solarData.blocks_data && Object.keys(solarData.blocks_data).length > 0) {

      let meterIndex = 0;

      let _tsHourlyEnergy = []
      let _tsHourlyPower = []
      let _tsDailyEnergy = []
      let _tsDailyPower = []
      let _tsMonthlyEnergy = []
      let _tsMonthlyPower = []

      // Iterate through all blocks
      Object.keys(solarData.blocks_data).forEach(blockId => {
        const blockData = solarData.blocks_data[blockId];

        // Iterate through all meters, in case somehow there are more than one meter
        if (blockData.meters_data) {
          Object.keys(blockData.meters_data).forEach(meterId => {
            const meterData = blockData.meters_data[meterId];

            // Latest data
            if (meterData.meter_data_latest) {
              const latestData = meterData.meter_data_latest;
              if (latestData.I_1) {
                setPhaseCurrent1(value => value + latestData.I_1);
              }
              if (latestData.I_2) {
                setPhaseCurrent2(value => value + latestData.I_2);
              }
              if (latestData.I_3) {
                setPhaseCurrent3(value => value + latestData.I_3);
              }
              if (latestData.P_active_total) {
                setPowerAvg(value => value - latestData.P_active_total);
              }
              const powerPeakToday = pickPeak(latestData.P_active_max_td, latestData.P_active_min_td);
              if (powerPeakToday) {
                setPowerMaxToday(value => value - powerPeakToday);
              }
              if (latestData.E_active_td) {
                setEnergyToday(value => value - latestData.E_active_td);
              }
            }

            // Time-series hourly
            // Go through the hourly data, put into the 24h plot
            if (meterData.meter_data_ts_hour) {
              const dataTsHourly = meterData.meter_data_ts_hour;
              // console.log(dataTsHourly);
              _tsHourlyEnergy.push(
                {
                  x: [],
                  y: [],
                  name: "Energy production"
                });

              _tsHourlyPower.push(
                {
                  x: [],
                  y: [],
                  name: "Peak power"
                });

              // Iterate through all epochs
              Object.keys(dataTsHourly).forEach(epoch => {
                const epochData = dataTsHourly[epoch];
                // console.log(epochData);
                // Keep every timestamped epoch (null = gap) so all bar traces share the same x positions
                if (epochData.local_datetime_str) {
                  _tsHourlyEnergy[meterIndex]["x"].push(epochData.local_datetime_str);
                  _tsHourlyEnergy[meterIndex]["y"].push(epochData.E_active != null ? -epochData.E_active : null); // Flip sign: production is negative by convention
                }
                const powerPeak = pickPeak(epochData.P_active_max, epochData.P_active_min);
                if (powerPeak) {
                  _tsHourlyPower[meterIndex]["x"].push(epochData.local_datetime_str);
                  _tsHourlyPower[meterIndex]["y"].push(-powerPeak); // Flip sign: production is negative by convention
                }
              }); // Iterate through all epochs
            }


            // Time-series daily
            // Go through the daily data, put into the 30 days plot
            if (meterData.meter_data_ts_day) {
              const dataTsDaily = meterData.meter_data_ts_day;
              // console.log(dataTsMonthly);
              _tsDailyEnergy.push(
                {
                  x: [],
                  y: [],
                  name: "Energy production"
                });

              _tsDailyPower.push(
                {
                  x: [],
                  y: [],
                  name: "Peak power"
                });

              // Iterate through all epochs
              Object.keys(dataTsDaily).forEach(epoch => {
                const epochData = dataTsDaily[epoch];
                // console.log(epochData);
                // Keep every timestamped epoch (null = gap) so all bar traces share the same x positions
                if (epochData.local_datetime_str) {
                  _tsDailyEnergy[meterIndex]["x"].push(epochData.local_datetime_str);
                  _tsDailyEnergy[meterIndex]["y"].push(epochData.E_active != null ? -epochData.E_active : null); // Flip sign: production is negative by convention
                }
                const powerPeak = pickPeak(epochData.P_active_max, epochData.P_active_min);
                if (powerPeak) {
                  _tsDailyPower[meterIndex]["x"].push(epochData.local_datetime_str);
                  _tsDailyPower[meterIndex]["y"].push(-powerPeak); // Flip sign: production is negative by convention
                }
              }); // Iterate through all epochs
            }

            // Time-series monthly
            if (meterData.meter_data_ts_month) {
              const dataTsMonthly = meterData.meter_data_ts_month;
              // console.log(dataTsMonthly);
              _tsMonthlyEnergy.push(
                {
                  x: [],
                  y: [],
                  name: "Energy production"
                });

              _tsMonthlyPower.push(
                {
                  x: [],
                  y: [],
                  name: "Peak power"
                });

              // Iterate through all epochs
              Object.keys(dataTsMonthly).forEach(epoch => {
                const epochData = dataTsMonthly[epoch];
                // console.log(epochData);
                // Keep every timestamped epoch (null = gap) so all bar traces share the same x positions
                if (epochData.local_datetime_str) {
                  _tsMonthlyEnergy[meterIndex]["x"].push(epochData.local_datetime_str);
                  _tsMonthlyEnergy[meterIndex]["y"].push(epochData.E_active != null ? -epochData.E_active : null); // Flip sign: production is negative by convention
                }
                const powerPeak = pickPeak(epochData.P_active_max, epochData.P_active_min);
                if (powerPeak) {
                  _tsMonthlyPower[meterIndex]["x"].push(epochData.local_datetime_str);
                  _tsMonthlyPower[meterIndex]["y"].push(-powerPeak); // Flip sign: production is negative by convention
                }
              }); // Iterate through all epochs
            }

            meterIndex = meterIndex + 1;

          }) // Iterate through all meters
        }
      }) // Iterate through all blocks 

      // console.log(_tsHourlyEnergy);
      setTsHourlyEnergy(_tsHourlyEnergy);
      setTsHourlyPower(_tsHourlyPower);
      setTsDailyEnergy(_tsDailyEnergy);
      setTsDailyPower(_tsDailyPower);
      setTsMonthlyEnergy(_tsMonthlyEnergy);
      setTsMonthlyPower(_tsMonthlyPower);

      setDataValid(true);
    }
  }

  useEffect(() => {
    if (pollingCounter != 0) {
      runRoutine();
    }
  }, [pollingCounter]);


  useEffect(() => {
    processSolarData(solarData);
  }, [solarData])


  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        pt: 2,
        gap: 2,
      }}
    >
      {(solarData != null && dataValid) ?
        <Box sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "repeat(2, minmax(0, 1fr))" },
          gap: 2,
        }}>
          <CurrentCard
            current1={phaseCurrent1}
            current2={phaseCurrent2}
            current3={phaseCurrent3}
            powerAvg={powerAvg}
            powerMaxToday={powerMaxToday}
            energyToday={energyToday}
          />
          <DayCard
            seriesEnergy={tsHourlyEnergy}
            seriesPeakPower={tsHourlyPower}
          />
          <MonthCard
            seriesEnergy={tsDailyEnergy}
            seriesPeakPower={tsDailyPower}
          />
          <YearCard
            seriesEnergy={tsMonthlyEnergy}
            seriesPeakPower={tsMonthlyPower}
          />
        </Box>
        :
        <UnavailableCard />
      }

      {solarData && isAdmin && <RawDataPanel data={solarData} />}
    </Box>
  )
}

export default SolarPage;